import {Trans} from '@lingui/macro';
import {isUndefined} from 'lodash';
import React, {FC, useCallback, useEffect, useRef} from 'react';

import {useInitialEffect} from '../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {useDebounce, usePrevious} from '../../../../../../srcCommon/helpers/hookHelpers';
import {
  isWildcardToken,
  SearchToken,
  splitSearchQuery,
} from '../../../../../../srcCommon/inboxes/search/searchHelpers';
import {buildCancelTokenSource, CancelTokenSource} from '../../../../../helpers/asyncCancelHelpers';
import {useAppDispatch, useAppSelector, wrapActionCreator} from '../../../../../helpers/reduxHelpers';
import {
  isSuggestionMatchEmail,
  isSuggestionMatchInternal,
  SearchSuggestionMatchModel,
} from '../../../../../models/api/searchSuggestionMatchModel';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {buildPublicId} from '../../../../../models/applications/publicIdTypes';
import {InboxesSearchLocationsEnum} from '../../../../../models/url/inboxesUrlModel';
import {
  clearSearchSuggestionsAction,
  fetchSearchSuggestionsAsyncAction,
} from '../../../../../store/panels/cells/search/cellsPanelSearchActions';
import {getCellsSearchSuggestions} from '../../../../../store/panels/cells/search/cellsPanelSearchSelectors';
import {InboxesParameters} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {InteractiveDateTimeFormatterContainer} from '../../../../common/formatters/dateTime/interactiveDateTimeFormatterContainer';
import {TeammatesFormatter} from '../../../../common/formatters/teammatesFormatter';
import {Skeleton, SkeletonColorSchemesEnum} from '../../../../common/skeleton';
import {renderTextSubject} from '../../../cells/content/cell/cellMiddle';
import {
  InboxesSearchSuggestionsBaseProps,
  InboxesSuggestionsDropdownItemIcon,
  InboxesSuggestionsDropdownSection,
  InboxesSuggestionsDropdownSectionTitle,
  InboxesSuggestionsDropdownSectionWrapper,
  InboxesSuggestionsDropdownTopResultDate,
  InboxesSuggestionsDropdownTopResultItem,
  InboxesSuggestionsDropdownTopResultRecipients,
  InboxesSuggestionsDropdownTopResultSubject,
  SEARCH_DROPDOWN_DEBOUNCE_WAIT_MS,
} from './inboxesSearchSuggestionsDropdownCommons';

/*
 * Constants.
 */

const TEAMMATES_LIMIT = 3;

/*
 * Props.
 */

interface InboxesSearchSuggestionsDropdownTopResultsProps extends InboxesSearchSuggestionsBaseProps {
  user: TeammateModel;
  teammates: ReadonlyArray<TeammateModel>;
  parameters: InboxesParameters;
  selectedSearchFilter: InboxesSearchLocationsEnum;
}

/*
 * Component.
 */

export const InboxesSearchSuggestionsDropdownTopResults: FC<
  InboxesSearchSuggestionsDropdownTopResultsProps
> = ({user, teammates, query, activeToken, parameters, selectedSearchFilter, onSuggestionSelect}) => {
  const trimmedQuery = query?.trim();
  const previousTrimmedQuery = usePrevious(trimmedQuery);

  const suggestions = useAppSelector(getCellsSearchSuggestions);

  const dispatch = useAppDispatch();
  const fetchSuggestionsAsync = wrapActionCreator(dispatch, fetchSearchSuggestionsAsyncAction);
  const clearSuggestions = wrapActionCreator(dispatch, clearSearchSuggestionsAction);

  const cancelTokenSourceRef = useRef<CancelTokenSource>();
  const maybeCancelRequest = (shouldClearSuggestions?: boolean) => {
    if (shouldClearSuggestions) {
      clearSuggestions();
    }

    const currentCancelTokenSource = cancelTokenSourceRef.current;
    return currentCancelTokenSource && currentCancelTokenSource.cancel();
  };

  useInitialEffect(() => {
    fetchItemsAsync(activeToken);

    return () => maybeCancelRequest(true);
  });

  const fetchItemsAsync = async (searchToken: SearchToken) => {
    // Cancel any existing request.
    maybeCancelRequest();

    const cancelTokenSource = buildCancelTokenSource();
    cancelTokenSourceRef.current = cancelTokenSource;

    // If activeToken is a wildcard token, do not fetch suggestions.
    if (isWildcardToken(searchToken, query)) {
      return;
    }

    fetchSuggestionsAsync(parameters, selectedSearchFilter, query, cancelTokenSource.token);
  };

  const fetchItemsAsyncDebounced = useDebounce(fetchItemsAsync, SEARCH_DROPDOWN_DEBOUNCE_WAIT_MS);

  const renderTeammates = useCallback(
    (suggestion: SearchSuggestionMatchModel) => {
      const filteredTeammates = teammates.filter((teammate) =>
        (suggestion.teammates || []).includes(teammate.id),
      );

      /**
       * For internal discussions, we need to render teammate(s).
       * If filteredTeammates is empty, it means the user is the only teammate in the conversation.
       * In this case, add the user in the array to display its name.
       */
      if (filteredTeammates.length === 0) {
        filteredTeammates.push(user);
      }

      // If there's a teammate for which givenName, familyName or alias starts with query, we should render it as the first one.
      const promotedFilteredTeammates = maybePromoteTeammate(filteredTeammates, query);

      const shouldShowFirstNameOnly = promotedFilteredTeammates.length > TEAMMATES_LIMIT;

      return (
        <TeammatesFormatter
          teammates={promotedFilteredTeammates}
          limit={TEAMMATES_LIMIT}
          showFirstNameOnly={shouldShowFirstNameOnly}
        />
      );
    },
    [query, teammates, user],
  );

  useEffect(() => {
    // Fetch new items only if trimmedQuery changed.
    if (trimmedQuery !== previousTrimmedQuery) {
      fetchItemsAsyncDebounced(activeToken);
    }
  }, [activeToken, fetchItemsAsyncDebounced, previousTrimmedQuery, trimmedQuery]);

  /**
   * Return early if:
   *   - suggestions is not defined OR
   *   - query is not defined OR
   *   - trimmed query is empty OR
   *   - query is only `modifier:value` OR
   *   - activeToken is a wildcard token.
   */

  if (
    !suggestions ||
    !trimmedQuery ||
    trimmedQuery.length === 0 ||
    hasOnlyOneModifier(trimmedQuery) ||
    isWildcardToken(activeToken, query)
  ) {
    return null;
  }

  /**
   * If `suggestions.results` is not defined, it means we did not fetch suggestions yet.
   * In this case, we want to display 4 loading suggestions.
   * If `suggestions.results` is defined but its length equals 0, we also want to display this.
   */
  if (!suggestions.results || suggestions.results.length === 0) {
    if (suggestions.isLoading) {
      return (
        <InboxesSuggestionsDropdownSectionWrapper>
          <InboxesSuggestionsDropdownSection>
            {renderSectionTitle()}
            {Array.from({length: 4}).map((_, index) => renderSkeleton(index))}
          </InboxesSuggestionsDropdownSection>
        </InboxesSuggestionsDropdownSectionWrapper>
      );
    }
    return null;
  }

  return (
    <InboxesSuggestionsDropdownSectionWrapper>
      <InboxesSuggestionsDropdownSection>
        {renderSectionTitle()}
        {suggestions.results.map((result) =>
          suggestions.isLoading ? (
            renderSkeleton(result.id)
          ) : (
            <InboxesSuggestionsDropdownTopResultItem
              onSelect={(e, key) =>
                onSuggestionSelect(createOnSelectValue(result), key, {
                  insertWhiteSpace: true,
                  replaceWholeQuery: true,
                  shouldSaveOriginalQuery: true,
                  isConversationSuggestion: true,
                })
              }
              key={result.id}
            >
              <InboxesSuggestionsDropdownItemIcon
                iconName={isSuggestionMatchEmail(result) ? 'channelEmailSmall' : 'discussionFilledSmall'}
              />
              <InboxesSuggestionsDropdownTopResultSubject>
                {renderTextSubject(result.subject, result.messageType)}
              </InboxesSuggestionsDropdownTopResultSubject>
              <InboxesSuggestionsDropdownTopResultDate>
                <InteractiveDateTimeFormatterContainer value={result.date} />
              </InboxesSuggestionsDropdownTopResultDate>
              <InboxesSuggestionsDropdownTopResultRecipients>
                {isSuggestionMatchInternal(result) ? renderTeammates(result) : result.recipient}
              </InboxesSuggestionsDropdownTopResultRecipients>
            </InboxesSuggestionsDropdownTopResultItem>
          ),
        )}
      </InboxesSuggestionsDropdownSection>
    </InboxesSuggestionsDropdownSectionWrapper>
  );
};

/*
 * Helpers.
 */

function hasOnlyOneModifier(query: string) {
  const queryParts = splitSearchQuery(query);
  return queryParts.length === 1 && !isUndefined(queryParts[0].token?.modifier);
}

function renderSectionTitle() {
  return (
    <InboxesSuggestionsDropdownSectionTitle>
      <Trans id="inboxes-search-top-results">Top results</Trans>
    </InboxesSuggestionsDropdownSectionTitle>
  );
}

function renderSkeleton(key: number) {
  return (
    <InboxesSuggestionsDropdownTopResultItem key={key}>
      <InboxesSuggestionsDropdownItemIcon />
      <InboxesSuggestionsDropdownTopResultSubject>
        <Skeleton width="80%" />
      </InboxesSuggestionsDropdownTopResultSubject>
      <InboxesSuggestionsDropdownTopResultRecipients>
        <Skeleton width="60%" scheme={SkeletonColorSchemesEnum.GREY10} />
      </InboxesSuggestionsDropdownTopResultRecipients>
    </InboxesSuggestionsDropdownTopResultItem>
  );
}

function createOnSelectValue(suggestion: SearchSuggestionMatchModel) {
  const publicId = buildPublicId('conversation', suggestion.id);
  return `id:${publicId}`;
}

function maybePromoteTeammate(teammates: ReadonlyArray<TeammateModel>, query?: string) {
  // If query is not defined, return the original array.
  if (!query) {
    return teammates;
  }

  // We'll stop on first match here to avoid iterating on all elements.
  const matchedTeammateIndex = teammates.findIndex(({givenName, familyName, alias}) =>
    query
      .toLowerCase()
      .split(' ')
      .some(
        (part) =>
          givenName.toLowerCase().startsWith(part) ||
          familyName.toLowerCase().startsWith(part) ||
          alias.toLowerCase().startsWith(part),
      ),
  );

  // If no teammate matches the query, return the original array.
  if (matchedTeammateIndex === -1) {
    return teammates;
  }

  // Otherwise, put matched teammate at the first position and concatenate others.
  return teammates.reduce(
    (acc, teammate, index) => (index === matchedTeammateIndex ? acc : [...acc, teammate]),
    [teammates[matchedTeammateIndex]],
  );
}
